// ─── Calendriers : possédés ∪ accès accordés (modèle propriétaire) ───
// Modèle (Phase 1) :
//   • Un ESPACE appartient à un propriétaire ; SEUL le propriétaire voit/configure
//     l'espace. Les membres ne « voient » pas l'espace — ils voient les ÉLÉMENTS
//     (ici les calendriers) auxquels le propriétaire leur a donné accès.
//   • La liste des calendriers d'un utilisateur = calendriers des espaces qu'il
//     POSSÈDE ∪ calendriers où il a un ACCÈS accordé (matrice `acces`, miroir
//     `accesUids`), à travers TOUS les espaces. Plus de notion d'« espace actif ».
//   • Superposition PERSONNELLE MoPCal inchangée (par utilisateur).
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAuth, useEspace, ecouterTenantsHeritables, useJetons, useQuotaElements, choisirElements, messageJetons } from '@mop/core';
import {
  ecouterCalendriersParEspaces, ecouterCalendriersAcces,
  creerCalendrierSuite, renommerCalendrier,
} from './services/calendriersEspace';
import { supprimerCalendrierSuite } from './services/calendriersEspace';
import { inventorierCalendrier } from './services/suppressionCalendrier';
import FeuilleCalendrier from './FeuilleCalendrier';
import FicheCalendrier from './FicheCalendrier';

const Ctx = createContext(null);
export const useCalendrierActif = () => useContext(Ctx) || {
  calendriers: [], calendriersTous: [], calIdActif: null, setCalIdActif: () => {}, espaceId: null,
  estVisible: () => true, toggleVisible: () => {}, couleurDe: () => '#6C4FE6', visiblesIds: [],
  heritables: [], estPerso: () => false, togglePerso: () => {},
  ouvrirEditionCalendrier: () => {}, fermerEditionCalendrier: () => {},
  ouvrirFicheCalendrier: () => {},
  pret: false, aCalendrierEspace: false, peutCreer: false, erreurLectureCal: '',
};

const PALETTE = ['#6C4FE6', '#E5397B', '#2E7D32', '#F57C00', '#0097A7', '#7B1FA2', '#5D4037', '#00838F'];
const cleActif = (u) => `mop:calActif:u:${u}`;      // actif par UTILISATEUR (multi-espaces)
const cleMontres = (u) => `mop:calMontres:u:${u}`;  // montrés en plus de l'actif
const clePerso = (u) => `mop:calPerso:${u}`;        // superposition perso MoPCal

const estProprio = (e, uid) => !!e && (e.creePar === uid || (e.membres && e.membres[uid] === 'proprietaire'));
const estCalIvv = (id) => id === 'ivv' || String(id || '').startsWith('ivv-cal-');

export function CalendrierActifProvider({ children }) {
  const { user } = useAuth();
  const { espaces = [], espaceActif, assurerEspace } = useEspace();
  const uid = user?.uid || null;

  // Espaces que JE possède (proprietaire) — seul cadre où je crée des calendriers.
  const espacesPossedes = useMemo(() => (espaces || []).filter(e => estProprio(e, uid)), [espaces, uid]);
  const ownedIds = useMemo(() => espacesPossedes.map(e => e.id), [espacesPossedes]);
  // Espace où créer un nouveau calendrier : l'actif si je le possède, sinon le 1er possédé.
  const espacePourCreer = (ownedIds.includes(espaceActif?.id) ? espaceActif?.id : ownedIds[0]) || null;

  const [owned, setOwned] = useState([]);     // calendriers de mes espaces possédés
  const [acces, setAcces] = useState([]);     // calendriers où j'ai un accès accordé
  const [ownedPret, setOwnedPret] = useState(false);
  const [accesPret, setAccesPret] = useState(false);
  const [heritables, setHeritables] = useState([]);
  const [choixActif, setChoixActif] = useState('');   // calId actif (par utilisateur)
  const [montres, setMontres] = useState([]);          // calIds montrés en plus de l'actif
  const [perso, setPerso] = useState([]);              // calIds MoPCal perso affichés
  const [editCal, setEditCal] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  // Ce qui a rate a la creation. Sans cet etat, un echec (quota serveur, espace
  // manquant, reseau) laissait l'utilisateur devant un ecran inchange.
  const [erreurCal, setErreurCal] = useState('');
  // Erreur de LECTURE des calendriers (owned/acces) — distincte d'`erreurCal`
  // (création). Sans elle, un refus de règle donnait `calendriers = []` :
  // indiscernable d'un espace qui n'a simplement aucun calendrier, et pouvait
  // même déclencher l'onboarding « crée ton premier calendrier » alors que
  // l'utilisateur en a — le pire des cinq points signalés le 11/08.
  const [erreurLectureCal, setErreurLectureCal] = useState('');

  // Calendriers de mes espaces possédés (temps réel, requête `in`).
  useEffect(() => {
    setOwnedPret(false);
    return ecouterCalendriersParEspaces(ownedIds, (l) => { setOwned(l); setOwnedPret(true); }, setErreurLectureCal);
  }, [ownedIds.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calendriers où j'ai un accès accordé (array-contains accesUids).
  useEffect(() => {
    setAccesPret(false);
    return ecouterCalendriersAcces(uid, (l) => { setAcces(l); setAccesPret(true); }, setErreurLectureCal);
  }, [uid]);

  useEffect(() => (uid ? ecouterTenantsHeritables(uid, setHeritables, () => setHeritables([])) : undefined), [uid]);

  // Préférences par utilisateur (actif, montrés, perso).
  useEffect(() => {
    if (!uid) return;
    AsyncStorage.getItem(cleActif(uid)).then(v => { if (v) setChoixActif(v); }).catch(() => {});
    AsyncStorage.getItem(cleMontres(uid)).then(v => { try { if (v) setMontres(JSON.parse(v)); } catch {} }).catch(() => {});
    AsyncStorage.getItem(clePerso(uid)).then(v => { try { if (v) setPerso(JSON.parse(v)); } catch {} }).catch(() => {});
  }, [uid]);

  // Liste fusionnée : possédés (prioritaires) ∪ accès ∪ perso MoPCal, dédupliqués.
  const calendriersBruts = useMemo(() => {
    const parId = new Map();
    // La projection doit porter la PROPRIÉTÉ (`creePar`) et les ACCÈS
    // (`accesN`) : sans eux, la fiche croit qu'un calendrier appartient à
    // quelqu'un d'autre, le 🎁 disparaît et tout passe en « lecture seule ».
    const pousser = (c, source) => { if (!parId.has(c.id)) parId.set(c.id, {
      id: c.id, source, nom: c.nom || 'Calendrier', emoji: c.emoji || '📅',
      type: c.type, couleur: c.couleur, espaceId: c.espaceId,
      creePar: c.creePar || c.ownerUid || null, creeParNom: c.creeParNom || '', accesN: c.accesN || null,
      // Les LIEUX vivent dans le doc calendrier : sans eux dans la projection,
      // le selecteur du formulaire d'evenement recevait une liste vide alors
      // que l'ecran Lieux, qui lit le doc en direct, les affichait. Meme piege
      // que `creePar`/`accesN` oublies auparavant.
      lieux: Array.isArray(c.lieux) ? c.lieux : [],
    }); };
    owned.forEach(c => pousser(c, 'suite'));
    acces.forEach(c => pousser(c, 'acces'));
    heritables.filter(h => perso.includes(h.id)).forEach(h => pousser({ ...h, emoji: h.emoji || '📆' }, 'mopcal'));
    let tous = Array.from(parId.values());
    // Calendriers IVV : masqués par défaut (bascule Profil des valideurs) ;
    // bascule ON → seuls ceux NON masqués individuellement (prefIvvMasques,
    // sélection conservée quand tout est coupé).
    if (!user?.prefVoirIvv) tous = tous.filter(c => !estCalIvv(c.id));
    else {
      const m = user?.prefIvvMasques || [];
      tous = tous.filter(c => !estCalIvv(c.id) || !m.includes(c.id));
    }
    return tous.map((c, i) => ({ ...c, couleur: c.couleur || PALETTE[i % PALETTE.length] }));
  }, [owned, acces, heritables, perso.join('|'), user?.prefVoirIvv, (user?.prefIvvMasques || []).join('|')]);

  // ── Jetons : ce que je peux réellement OUVRIR ────────────────────
  // Chaque calendrier porte son droit (`droitJeton` : 'gestion' | 'vue' | null)
  // et la liste EXPOSÉE aux écrans ne garde que ceux qui en ont un. Le Profil,
  // lui, doit continuer à tout montrer pour permettre de choisir : il lit
  // `calendriersTous`.
  const { droitDe, droitImplicite, sansLimite, jamaisEnregistre, dotation, selection } = useJetons();
  // Éléments POSSÉDÉS, tous types : l'app les déclare au niveau racine
  // (`SourcesElementsProvider` + `useElementsPossedes`). On ne recompte SURTOUT
  // pas les calendriers ici — c'est ce comptage local, aveugle aux inventaires
  // et aux plans, qui annonçait « 2/2 » à un compte possédant 4 éléments.
  const quota = useQuotaElements();
  const { possedes: possedesTotal, peutCreer: peutCreerElement } = quota;

  // La sélection suit le cycle de vie des éléments : créer en ajoute un (le
  // jeton était libre, sinon la création est refusée en amont), supprimer le
  // retire. Sans cela, un jeton resterait immobilisé sur un calendrier disparu.
  const ajouterASelection = async (id) => {
    if (sansLimite) return;
    const cle = `calendrier:${id}`;
    const base = jamaisEnregistre
      ? Object.entries(droitImplicite(calendriersBruts.map(c => `calendrier:${c.id}`)))
          .map(([c, nature]) => ({ cle: c, nature }))
      : selection;
    if (base.some(x => x.cle === cle && !x.pour)) return;
    // Un element qu'on vient de creer se GERE : il prend un jeton ⚙️. Si le
    // serveur refuse (plus de jeton libre), on le dit — le calendrier existe
    // mais n'apparaitra pas dans la liste tant que rien n'est libere.
    await choisirElements([...base, { cle, nature: 'gestion' }]);
  };
  const retirerDeSelection = async (id) => {
    if (sansLimite || jamaisEnregistre) return;
    const cle = `calendrier:${id}`;
    if (!selection.some(x => x.cle === cle)) return;
    try { await choisirElements(selection.filter(x => x.cle !== cle)); } catch (_) {}
  };
  const calendriersTous = useMemo(() => {
    // Rien d'enregistré : droit IMPLICITE (les premiers, dans la limite des
    // jetons) — on ne montre jamais plus que ce que le forfait permet.
    const implicite = jamaisEnregistre
      ? droitImplicite(calendriersBruts.map(c => `calendrier:${c.id}`)) : {};
    return calendriersBruts.map(c => ({
      ...c,
      droitJeton: sansLimite ? 'gestion'
        : (jamaisEnregistre ? (implicite[`calendrier:${c.id}`] || null) : droitDe(`calendrier:${c.id}`)),
    }));
  }, [calendriersBruts, droitDe, droitImplicite, sansLimite, jamaisEnregistre]);
  // On ne voit QUE ce qui est retenu : sans droit, le calendrier n'existe pas
  // pour les écrans (il reste listé dans la sélection et le Profil).
  const calendriers = useMemo(
    () => (sansLimite ? calendriersTous : calendriersTous.filter(c => c.droitJeton)),
    [calendriersTous, sansLimite]);

  const calIdActif = (choixActif && calendriers.some(c => c.id === choixActif))
    ? choixActif
    : (calendriers.find(c => c.source === 'suite')?.id || calendriers[0]?.id || null);

  // Visibilité : par défaut SEUL l'actif s'affiche.
  const estVisible = (id) => id === calIdActif || montres.includes(id);
  const visiblesIds = calendriers.filter(c => estVisible(c.id)).map(c => c.id);
  const couleurDe = (id) => calendriers.find(c => c.id === id)?.couleur || '#6C4FE6';

  const setCalIdActif = (id) => {
    if (!uid) return;
    setChoixActif(id || '');
    AsyncStorage.setItem(cleActif(uid), id || '').catch(() => {});
  };
  const toggleVisible = (id) => {
    if (!uid || id === calIdActif) return;
    const set = new Set(montres); set.has(id) ? set.delete(id) : set.add(id);
    const arr = Array.from(set); setMontres(arr);
    AsyncStorage.setItem(cleMontres(uid), JSON.stringify(arr)).catch(() => {});
  };
  const estPerso = (id) => perso.includes(id);
  const togglePerso = (id) => {
    if (!uid) return;
    const set = new Set(perso); set.has(id) ? set.delete(id) : set.add(id);
    const arr = Array.from(set); setPerso(arr);
    AsyncStorage.setItem(clePerso(uid), JSON.stringify(arr)).catch(() => {});
  };

  // Fiche d'information — rendue au niveau RACINE, comme l'édition : une
  // `Feuille` posée à l'intérieur d'une carte du Profil reste prisonniere de
  // son conteneur et s'affiche tronquee au milieu de l'ecran.
  const [ficheCal, setFicheCal] = useState(null);
  const ouvrirFicheCalendrier = (cal) => setFicheCal(cal || null);

  // Création / édition d'un calendrier — dans l'espace que JE possède.
  const ouvrirEditionCalendrier = (cal) => setEditCal(cal || { nom: '', emoji: '📅', type: 'general' });
  const fermerEditionCalendrier = () => setEditCal(null);
  async function enregistrerCal({ nom, emoji, type }) {
    if (editBusy) return;
    setEditBusy(true);
    setErreurCal('');
    try {
      if (editCal?.id) await renommerCalendrier(editCal.id, { nom, emoji, type });
      // Masquer le « ＋ » ne suffit pas : une feuille déjà ouverte, ou un quota
      // franchi entre-temps (élément créé sur un autre appareil), passerait au
      // travers. On revérifie donc au moment d'écrire.
      else if (sansLimite || peutCreerElement) {
        // Un calendrier a besoin d'un espace pour exister. Plutot que de ne
        // RIEN faire quand l'utilisateur n'en possede aucun (le ＋ repondait
        // dans le vide), on cree son espace a son nom : c'est le contenant
        // evident, il pourra le renommer.
        // `assurerEspace` (Core) cree l'espace au nom de l'utilisateur s'il n'en
        // possede aucun : meme geste pour tous les modules.
        const espaceId = espacePourCreer || await assurerEspace();
        const idCree = await creerCalendrierSuite(espaceId, { nom, emoji, type }, { uid, nom: user?.prenom || user?.email || '' });
        // Un calendrier qu'on vient de créer se GERE : il prend un jeton ⚙️.
        if (idCree) { setCalIdActif(idCree); await ajouterASelection(idCree); }
      }
      setEditCal(null);
    } catch (e) {
      // Le calendrier a pu etre cree ET l'ajout a la file echouer : on garde la
      // feuille fermee mais on dit ce qui s'est passe.
      setErreurCal(messageJetons ? messageJetons(e) : (e?.message || String(e)));
    }
    setEditBusy(false);
  }

  // Suppression : on libère d'abord le jeton, sinon il resterait immobilisé sur
  // un calendrier qui n'existe plus.
  const supprimerCalendrier = async (id, nomCalendrier) => {
    await retirerDeSelection(id);
    await supprimerCalendrierSuite(id, { uid, nom: user?.prenom || user?.email || '', nomCalendrier });
  };

  const value = useMemo(() => ({
    // `calendriers` = ce qui est OUVRABLE (filtré par les jetons) ;
    // `calendriersTous` = tout ce que je possède ou qu'on m'a partagé, pour
    // l'écran de sélection et la gestion dans le Profil.
    espaceId: espacePourCreer, calendriers, calendriersTous, visiblesIds, calIdActif,
    setCalIdActif, estVisible, toggleVisible, couleurDe,
    heritables, estPerso, togglePerso, ouvrirEditionCalendrier, fermerEditionCalendrier, ouvrirFicheCalendrier,
    supprimerCalendrier, inventorierCalendrier,
    pret: ownedPret && accesPret,
    aCalendrierEspace: calendriers.length > 0,
    // Créer = POSSÉDER un élément de plus : borné par le maximum de jetons,
    // tous types confondus. Retirer un calendrier de la liste des visibles ne
    // donne donc PAS le droit d'en créer un autre — il faut en supprimer un.
    peutCreer: !!espacePourCreer && (sansLimite || peutCreerElement),
    plusDeJetons: !sansLimite && !peutCreerElement,
    quotaElements: quota,
    erreurCal, effacerErreurCal: () => setErreurCal(''),
    erreurLectureCal,
  }), [espacePourCreer, calendriers, calendriersTous, choixActif, montres, perso, heritables, calIdActif, ownedPret, accesPret, sansLimite, quota, erreurCal, erreurLectureCal]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {/* Modale d'édition de calendrier — au niveau RACINE (hors ScrollView). */}
      <FeuilleCalendrier visible={!!editCal} initial={editCal} busy={editBusy}
        onEnregistrer={enregistrerCal} onFermer={() => setEditCal(null)} />
      {/* Fiche d'information — RACINE elle aussi (cf. commentaire ci-dessus). */}
      <FicheCalendrier cal={ficheCal} actif={!!ficheCal && calIdActif === ficheCal.id}
        onFermer={() => setFicheCal(null)}
        onRendreActif={() => { setCalIdActif(ficheCal.id); setFicheCal(null); }}
        onEditer={ficheCal?.source === 'suite'
          ? () => { const c = ficheCal; setFicheCal(null);
              ouvrirEditionCalendrier({ id: c.id, nom: c.nom, emoji: c.emoji || '📅', type: c.type || 'general' }); }
          : undefined} />
    </Ctx.Provider>
  );
}
